// Shared status logic for the bookings page (used by ManageBookings and BookingRow).
// Statuses returned by the server for this page: booking_request, invoice_sent, canceled.
// (Confirmed bookings live on /my_bookings.)

export type Tone = "warning" | "accent" | "danger" | "neutral";

export interface StatusView {
  label: string;
  tone: Tone;
  needsAction: boolean;
}

export interface TabDef {
  value: string;
  label: string;
}

export const TONE_CLASSES: Record<Tone, string> = {
  warning: "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  accent: "bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  danger: "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  neutral: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300",
};

// The status that needs the viewer to do something next.
export const actionStatus = (isHost: boolean) =>
  isHost ? "booking_request" : "invoice_sent";

export const getStatusView = (
  status: string | undefined,
  isHost: boolean
): StatusView => {
  const needsAction = status === actionStatus(isHost);

  if (status === "booking_request") {
    return isHost
      ? { label: "New request", tone: "warning", needsAction }
      : { label: "Waiting for host", tone: "accent", needsAction };
  }
  if (status === "invoice_sent") {
    return isHost
      ? { label: "Invoice sent · waiting for payment", tone: "accent", needsAction }
      : { label: "Host accepted · pay to confirm", tone: "warning", needsAction };
  }
  if (status === "canceled") {
    return { label: "Canceled", tone: "danger", needsAction: false };
  }

  // Any other status (for example "completed"): show it as plain text, no actions.
  const text = (status || "unknown").replace(/_/g, " ");
  return {
    label: text.charAt(0).toUpperCase() + text.slice(1),
    tone: "neutral",
    needsAction: false,
  };
};

export const getTabs = (isHost: boolean): TabDef[] =>
  isHost
    ? [
        { value: "all", label: "All" },
        { value: "booking_request", label: "Needs your answer" },
        { value: "invoice_sent", label: "Waiting for payment" },
        { value: "canceled", label: "Canceled" },
      ]
    : [
        { value: "all", label: "All" },
        { value: "invoice_sent", label: "Needs payment" },
        { value: "booking_request", label: "Waiting for host" },
        { value: "canceled", label: "Canceled" },
      ];